import ReferenceIcon from '@assets/icons/note.svg?react'
import RecipientIcon from '@assets/icons/user.svg?react'
import {
  CoreApplicationFeeAnnounce,
  CoreInput,
  CoreOnboardingAnnounce,
  CorePaymentInput,
  CoreSelect,
  MMButton,
  SendMoneyMethodInfo,
  SuccessPane
} from '@components'
import { useHeaderContext } from '@context/HeaderContext'
import { useSearchParams } from '@hooks'
import { toastSuccess } from '@utils'
import { InteracRecipientEdit } from '@views/recipients/elements/InteracRecipientEdit'
import AccountingCategorySelect from '@withdrawals/components/AccountingCategorySelect'
import styles from '@withdrawals/styles/WithdrawInitiate.module.scss'
import WalletTransferPreview from '@withdrawals/views/WalletTransferPreview'
import {
  MerchantAccountTypes,
  PaymentOptionStatuses,
  responseDoesNotContainsChallenge,
  selectMerchantAccountType,
  useAppSelector,
  useReturnToOrigin,
  useShouldPayApplicationFeeQry,
  useWalletTransferQry,
  WithdrawalSubFlows
} from 'mmfintech-backend-api'
import { GlobalContext, isValidObject, isValidString, OtpContext, tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

const InteractTransfer = () => {
  const { modalHide, modalShow } = useContext(GlobalContext)
  const { setOtpOnSuccess } = useContext(OtpContext)

  const { currentStep, setCurrentStep } = useHeaderContext()

  const { recipientId } = useSearchParams()

  const [result, setResult] = useState(null)
  const [promoCode, setPromoCode] = useState('')

  const history = useHistory()

  const accountType = useAppSelector(selectMerchantAccountType)
  const shouldPayApplicationFee = useShouldPayApplicationFeeQry()

  const { returnToOrigin } = useReturnToOrigin()
  const returnToOriginUrl = returnToOrigin({ recipientId })

  const {
    formValues,
    handleRecipientChange,
    handlePreview,
    handleInitiate,
    reloadRecipients,
    selectedAccount,
    selectedRecipient,
    setSelectedAccount,
    filteredRecipients,
    shouldShowWarning,
    accounts,
    accountsError,
    accountsFetching,
    recipientsError,
    recipientsFetching,
    previewFetching,
    previewError,
    resetMutations
  } = useWalletTransferQry({
    flowType: WithdrawalSubFlows.INTERAC,
    editRecipient: (id?: number) => handleEditRecipient(id),
    onPreviewSuccess: () => setCurrentStep(2),
    onInitiateSuccess: (response?: any) => handleInitiateSuccess(response),
    selectAddNew: false
  })

  const handleEditRecipient = (recipient?: any) => {
    const { currencyCode } = selectedAccount || {}

    modalShow({
      header:
        recipient?.paymentInstrumentId > 0
          ? tr('FRONTEND.RECIPIENTS.EDIT.TITLE', 'Recipient details')
          : tr('FRONTEND.RECIPIENTS.CREATE.TITLE', 'Create recipient'),
      content: (
        <InteracRecipientEdit
          recipientId={recipient?.paymentInstrumentId}
          currencyCode={currencyCode}
          onClose={modalHide}
          onSuccess={(response: any) => {
            toastSuccess(
              recipient?.paymentInstrumentId
                ? tr('FRONTEND.RECIPIENTS.EDIT.SUCCESS', 'Recipient updated.')
                : tr('FRONTEND.RECIPIENTS.CREATE.SUCCESS', 'Recipient successfully created.'),
              undefined,
              {
                duration: 3_000
              }
            )
            reloadRecipients(response?.paymentInstrumentId)
          }}
        />
      )
    })
  }

  const transferSuccess = (data: any) => {
    setResult(data)
    setCurrentStep(3)
  }

  const handleInitiateSuccess = (data: any): void => {
    if (responseDoesNotContainsChallenge(data)) {
      transferSuccess(data)
    }
  }

  const handleAdvancedInitiate = () => {
    setOtpOnSuccess(() => transferSuccess)
    handleInitiate(promoCode)
  }

  useEffect(() => {
    return () => {
      setCurrentStep(1)
      setResult(null)
      resetMutations()
    }
  }, [])

  return (
    <>
      {currentStep === 1 && (
        <div className={styles['send-money-container']} data-test='interac-transfer-container'>
          <div className='from-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.INTERAC.FROM', 'From')}</div>
            </div>
            <CorePaymentInput
              selectedOption={selectedAccount}
              setSelectedAccount={setSelectedAccount}
              dataTest={'deposit-payment-input-test'}
              options={accounts}
              enableMaxAmountButton
              paymentOption={selectedRecipient?.paymentOption}
              onChange={(value: any) => formValues.setValue('amount', value)}
              value={formValues.getValue('amount')}
              error={formValues.getError('amount')}
              isLoading={accountsFetching}
            />
          </div>

          <div className='to-container'>
            <div className='from-to-label-container'>
              <div className='from-to-label'>{tr('FRONTEND.WITHDRAW.INTERAC.TO', 'To')}</div>

              {!recipientId && accountType !== MerchantAccountTypes.PROSPECT && (
                <MMButton
                  type='button'
                  size='normal'
                  variant='tertiary'
                  leftIcon={RecipientIcon}
                  caption={tr('FRONTEND.RECIPIENTS.LIST.BUTTON_ADD', 'New recipient')}
                  onClick={() => handleEditRecipient()}
                  loading={previewFetching}
                  data-test='button-add-recipient'
                />
              )}
            </div>
            <CoreSelect
              type='default'
              data-test='destination-payment-instrument-id'
              label={tr('FRONTEND.WITHDRAW.INTERAC.RECIPIENT', 'Recipient')}
              options={filteredRecipients}
              onChange={handleRecipientChange}
              loading={recipientsFetching}
              disabled={recipientsFetching}
              LeftIcon={<RecipientIcon />}
              value={
                selectedRecipient
                  ? {
                      value: selectedRecipient?.paymentInstrumentId,
                      label: selectedRecipient?.name
                    }
                  : null
              }
            />
            {isValidObject(selectedRecipient) && isValidString(selectedRecipient.description) && (
              <div className='recipient-info-container'>
                <div className='recipient-description-wrapper'>
                  <div className='description-label'>
                    {tr('FRONTEND.WITHDRAW.INTERAC.RECIPIENT_DESCRIPTION', 'Recipient description')}
                  </div>
                  <div className='description'>{selectedRecipient.description}</div>
                </div>
              </div>
            )}
          </div>

          {isValidObject(selectedRecipient?.paymentOption) && (
            <SendMoneyMethodInfo
              paymentOption={selectedRecipient.paymentOption}
              currencyCode={selectedAccount?.currencyCode}
            />
          )}

          <CoreInput
            type='text'
            name='reference'
            data-test='reference'
            maxLength={50}
            label={tr('FRONTEND.WITHDRAW.INTERAC.REFERENCE', 'Reference')}
            {...formValues.registerInput('reference')}
            autoComplete='off'
            LeftIcon={<ReferenceIcon />}
          />

          <AccountingCategorySelect formValues={formValues} />

          {shouldShowWarning() && <CoreOnboardingAnnounce />}
          {shouldPayApplicationFee && <CoreApplicationFeeAnnounce />}

          <div className='form-actions'>
            <ErrorDisplay error={[accountsError, recipientsError, previewError]} />

            <MMButton
              type='button'
              size='large'
              variant='alternative'
              caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
              onClick={handlePreview}
              loading={previewFetching}
              disabled={
                accountsFetching ||
                recipientsFetching ||
                selectedRecipient?.paymentOption?.status !== PaymentOptionStatuses.AVAILABLE ||
                formValues.getFloat('amount') <= 0
              }
              data-test='button-continue'
              fullWidth
            />
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <WalletTransferPreview
          formValues={formValues}
          selectedAccount={selectedAccount}
          selectedRecipient={selectedRecipient}
          onSubmit={handleAdvancedInitiate}
          onCancel={() => setCurrentStep(1)}
          onApplyPromoCode={setPromoCode}
          subFlow={WithdrawalSubFlows.INTERAC}
        />
      )}

      {currentStep === 3 && <SuccessPane response={result} onClick={() => history.push(returnToOriginUrl)} />}
    </>
  )
}

export default InteractTransfer
